import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import * as Sentry from "@sentry/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
} from "recharts";
import { BsPeople, BsClock, BsPersonCheck, BsAward, BsGraphUpArrow, BsActivity, BsFilter, BsExclamationTriangle } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import { formatNumber, formatTime } from "../../../../utils/analyticsDashboardFormat";
import { axisCategoryTick, resolveEntityName } from "../../../../utils/analyticsChartLabels";
import { Select } from "../../../../components/ui/components/Input";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  WidgetTable,
  TableScroll,
  FilterRow,
  InfoNote,
  StatusPill,
  EmptyStateBox,
  MetricTooltip,
} from "./Widgets.styles";

// Timezone offset in minutes (e.g. -360 for UTC-6 / Mexico City).
const LOCAL_UTC_OFFSET_MIN = -new Date().getTimezoneOffset();

const UserPerformanceWidgets = ({ companyId, startDate, endDate, channelIds, dataEnrichment }) => {
  const [sourceFilter, setSourceFilter] = useState("ALL");
  const hasActiveSourceFilter = sourceFilter !== "ALL";
  const channelId = channelIds?.[0];

  const {
    data: filteredAgentPerformance,
    isLoading: filteredPerfLoading,
    error: filteredPerfError,
  } = useQuery(
    ["agent-performance-by-source", companyId, startDate, endDate, channelId, sourceFilter],
    () =>
      analyticsDashboardApi.getAgentPerformanceBySource({
        companyId,
        startDate,
        endDate,
        channelId,
        source: sourceFilter,
      }),
    { enabled: !!companyId && hasActiveSourceFilter }
  );

  const {
    data: assignedConversations,
    isLoading: assignedConvLoading,
    error: assignedConvError,
  } = useQuery(
    ["assigned-conversations", companyId, startDate, endDate, channelId],
    () => analyticsDashboardApi.getAssignedConversations({ companyId, startDate, endDate, channelId, timezoneOffset: LOCAL_UTC_OFFSET_MIN }),
    { enabled: !!companyId }
  );

  const assignedConversationsMap = useMemo(() => {
    const map = new Map();
    (assignedConversations?.data || []).forEach((item) => map.set(item.agentId, item.assignedConversations));
    return map;
  }, [assignedConversations]);

  const filteredPerformanceMap = useMemo(() => {
    if (!hasActiveSourceFilter || !filteredAgentPerformance?.data) return null;
    const map = new Map();
    filteredAgentPerformance.data.forEach((item) => map.set(item.userId, { totalFinished: item.totalFinished, avgRating: item.avgRating }));
    return map;
  }, [hasActiveSourceFilter, filteredAgentPerformance]);

  const {
    data: userAnalytics,
    isLoading: userLoading,
    error: userAnalyticsError,
  } = useQuery(
    ["clickhouse-user-analytics", companyId, startDate, endDate, channelId],
    () => analyticsDashboardApi.getClickHouseUserAnalytics({ companyId, startDate, endDate, channelId, timezoneOffset: LOCAL_UTC_OFFSET_MIN, limit: 10000 }),
    { enabled: !!companyId }
  );

  const {
    data: topUsers,
    isLoading: topUsersLoading,
    error: topUsersError,
  } = useQuery(
    ["clickhouse-top-users", companyId, startDate, endDate, channelId],
    () => analyticsDashboardApi.getClickHouseMaterializedView("daily_user_stats", { companyId, startDate, endDate, channelId, timezoneOffset: LOCAL_UTC_OFFSET_MIN, limit: 5000 }),
    { enabled: !!companyId }
  );

  const {
    data: agentPerformance,
    isLoading: agentPerfLoading,
    error: agentPerfError,
  } = useQuery(
    ["clickhouse-agent-performance", companyId, startDate, endDate, channelId],
    () => analyticsDashboardApi.getClickHouseMaterializedView("agent_performance_daily", { companyId, startDate, endDate, channelId, timezoneOffset: LOCAL_UTC_OFFSET_MIN, limit: 10000 }),
    { enabled: !!companyId }
  );

  const isLoading = userLoading || topUsersLoading || agentPerfLoading || assignedConvLoading || (hasActiveSourceFilter && filteredPerfLoading);
  const error =
    userAnalyticsError ||
    topUsersError ||
    agentPerfError ||
    assignedConvError ||
    (hasActiveSourceFilter && filteredPerfError);

  const userMetrics = useMemo(() => {
    if (!userAnalytics?.data) return null;
    const users = userAnalytics.data;
    const totalUsers = new Set(users.map((u) => u.user_id)).size;
    const totals = users.reduce(
      (acc, item) => ({
        totalOutgoingMessages: acc.totalOutgoingMessages + (item.outgoing_messages || 0),
        totalFinishedClients: acc.totalFinishedClients + (item.finished_clients || 0),
        totalResponsesAfterBot: acc.totalResponsesAfterBot + (item.responses_after_bot || 0),
        totalResponseTime: acc.totalResponseTime + (item.average_response_time_seconds || 0) * (item.outgoing_messages || 0),
        totalMessages: acc.totalMessages + (item.outgoing_messages || 0),
      }),
      { totalOutgoingMessages: 0, totalFinishedClients: 0, totalResponsesAfterBot: 0, totalResponseTime: 0, totalMessages: 0 }
    );
    const avgResponseTime = totals.totalMessages > 0 ? totals.totalResponseTime / totals.totalMessages : 0;
    return {
      activeUsers: totalUsers,
      totalFinishedClients: totals.totalFinishedClients,
      averageResponseTime: avgResponseTime,
      totalResponsesAfterBot: totals.totalResponsesAfterBot,
    };
  }, [userAnalytics]);

  const calculateChartHeight = (dataLength) => Math.min(Math.max(300, dataLength * 35), 600);

  const topUsersData = useMemo(() => {
    const rawData = topUsers?.data ?? topUsers;
    if (!Array.isArray(rawData) || rawData.length === 0) return [];
    const userMap = new Map();
    rawData.forEach((item) => {
      const userId = item.user_id;
      if (!userMap.has(userId)) {
        userMap.set(userId, { userId, outgoingMessages: 0, finishedClients: 0, avgResponseTime: 0, responsesAfterBot: 0, totalEntries: 0 });
      }
      const user = userMap.get(userId);
      user.outgoingMessages += item.outgoing_messages || 0;
      user.finishedClients += item.finished_clients || 0;
      user.responsesAfterBot += item.responses_after_bot || 0;
      user.avgResponseTime += item.avg_response_time_seconds || 0;
      user.totalEntries += 1;
    });
    return Array.from(userMap.values())
      .map((user) => ({
        ...user,
        avgResponseTime: user.totalEntries > 0 ? user.avgResponseTime / user.totalEntries : 0,
        userName: resolveEntityName(user.userId, dataEnrichment?.getUserName),
      }))
      .sort((a, b) => b.outgoingMessages - a.outgoingMessages)
      .slice(0, 10);
  }, [topUsers, dataEnrichment]);

  const responseTimeData = useMemo(() => {
    if (!userAnalytics?.data) return [];
    const userMap = new Map();
    userAnalytics.data.forEach((item) => {
      const userId = item.user_id;
      if (!userMap.has(userId)) userMap.set(userId, { userId, totalMessages: 0, totalFinished: 0, totalResponseTime: 0, entries: 0 });
      const user = userMap.get(userId);
      user.totalMessages += item.outgoing_messages || 0;
      user.totalFinished += item.finished_clients || 0;
      user.totalResponseTime += (item.average_response_time_seconds || 0) * (item.outgoing_messages || 0);
      user.entries += 1;
    });
    return Array.from(userMap.values())
      .filter((user) => user.totalMessages > 0)
      .map((user) => ({
        avgResponseTime: user.totalResponseTime / user.totalMessages,
        productivity: user.totalFinished,
        userName: resolveEntityName(user.userId, dataEnrichment?.getUserName),
      }))
      .filter((user) => user.avgResponseTime > 0 && user.avgResponseTime < 3600)
      .slice(0, 50);
  }, [userAnalytics, dataEnrichment]);

  const agentRatingsMap = useMemo(() => {
    const rawData = agentPerformance?.data ?? agentPerformance;
    if (!Array.isArray(rawData) || rawData.length === 0) return new Map();
    const agentMap = new Map();
    rawData.forEach((item) => {
      const agentId = item.finished_by;
      if (!agentId) return;
      if (!agentMap.has(agentId)) agentMap.set(agentId, { totalRating: 0, totalFinished: 0 });
      const agent = agentMap.get(agentId);
      const dayFinished = item.total_finished || 0;
      agent.totalRating += (item.avg_rating || 0) * dayFinished;
      agent.totalFinished += dayFinished;
    });
    const ratingsMap = new Map();
    agentMap.forEach((data, agentId) => {
      if (data.totalFinished > 0) ratingsMap.set(agentId, (data.totalRating / data.totalFinished / 5) * 100);
    });
    return ratingsMap;
  }, [agentPerformance]);

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { component: "UserPerformanceWidgets", op: "fetchUserPerformance" },
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <MetricsGrid columns={4}>
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i}>
            <SkeletonBlock style={{ height: 16, width: "50%", marginBottom: 12 }} />
            <SkeletonBlock style={{ height: 32 }} />
          </MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar rendimiento de usuarios</div>
      </EmptyStateBox>
    );
  }

  const tooltipStyle = {
    contentStyle: {
      background: "white",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
      fontFamily: "'Inter', sans-serif",
      fontSize: "0.82rem",
    },
    labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
    itemStyle: { color: "#4B5563" },
  };

  return (
    <WidgetSection>
      <MetricsGrid columns={4}>
        <MetricCard accentColor="#6366f1">
          <MetricCardHeader>
            <MetricLabel>
              Usuarios Activos
              <MetricTooltip text="Número de agentes distintos que enviaron al menos un mensaje en el período seleccionado." />
            </MetricLabel>
            <MetricIconWrap bg="#EEF2FF" color="#6366f1"><BsPeople /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(userMetrics?.activeUsers || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#f59e0b">
          <MetricCardHeader>
            <MetricLabel>
              Tiempo Promedio de Respuesta
              <MetricTooltip text="Promedio ponderado de tiempo (en segundos) entre el mensaje de entrada y la primera respuesta del agente." />
            </MetricLabel>
            <MetricIconWrap bg="#FFFBEB" color="#f59e0b"><BsClock /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatTime(userMetrics?.averageResponseTime || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Finalizadas
              <MetricTooltip text="Total de conversaciones cerradas por los agentes durante el período. Indica la productividad global del equipo." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#10b981"><BsPersonCheck /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(userMetrics?.totalFinishedClients || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#8b5cf6">
          <MetricCardHeader>
            <MetricLabel>
              Respuestas tras Bot
              <MetricTooltip text="Cantidad de mensajes enviados por agentes después de que el bot intervino. Mide la transición de bot a humano." />
            </MetricLabel>
            <MetricIconWrap bg="#F5F3FF" color="#8b5cf6"><BsActivity /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(userMetrics?.totalResponsesAfterBot || 0)}</MetricValue>
        </MetricCard>
      </MetricsGrid>

      <ChartCard>
        <ChartCardTitle>
          <BsAward /> Usuarios Más Activos
          <MetricTooltip text="Top 10 de agentes ordenados por mensajes enviados. Las barras muestran mensajes enviados y conversaciones finalizadas por agente." />
        </ChartCardTitle>
        <ResponsiveContainer width="100%" height={calculateChartHeight(topUsersData.length)}>
          <BarChart data={topUsersData} layout="vertical" margin={{ top: 5, right: 24, left: 30, bottom: 5 }} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="userName" width={160} tickFormatter={axisCategoryTick} tick={{ fontSize: 11, fill: "#6B7280" }} interval={0} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value, name) => [formatNumber(value), name === "outgoingMessages" ? "Mensajes Enviados" : "Conversaciones Finalizadas"]} {...tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
            <Bar dataKey="outgoingMessages" fill="#6366f1" name="Mensajes Enviados" radius={[0, 4, 4, 0]} />
            <Bar dataKey="finishedClients" fill="#10b981" name="Conversaciones Finalizadas" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard>
        <ChartCardTitle>
          <BsClock /> Tiempo de Respuesta vs Productividad
          <MetricTooltip text="Diagrama de dispersión: eje X = tiempo promedio de respuesta del agente; eje Y = conversaciones finalizadas. Los puntos en la esquina inferior derecha indican alta productividad con respuesta rápida." />
        </ChartCardTitle>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart data={responseTimeData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
            <XAxis type="number" dataKey="avgResponseTime" name="Tiempo Promedio de Respuesta (s)" tickFormatter={(value) => formatTime(value)} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis type="number" dataKey="productivity" name="Conversaciones Finalizadas" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value, name) => [name === "avgResponseTime" ? formatTime(value) : formatNumber(value), name === "avgResponseTime" ? "Tiempo Promedio de Respuesta" : "Conversaciones Finalizadas"]} {...tooltipStyle} />
            <Scatter dataKey="productivity" fill="#6366f1" name="Productividad del Agente" fillOpacity={0.75} />
          </ScatterChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard>
        <FilterRow style={{ justifyContent: "space-between", marginBottom: 16 }}>
          <ChartCardTitle style={{ margin: 0 }}>
            <BsGraphUpArrow /> Métricas de Productividad de Usuarios
            <MetricTooltip text="Tabla detallada por agente con conversaciones asignadas, mensajes enviados, conversaciones finalizadas, tiempos de respuesta, eficiencia y calificación promedio." />
          </ChartCardTitle>
          <FilterRow>
            <BsFilter />
            <Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={{ minWidth: 180 }}>
              <option value="ALL">Todas las fuentes</option>
              <option value="ORGANIC">Orgánico</option>
              <option value="FB_FORM">Facebook Forms</option>
              <option value="FB_ADS">Facebook Ads</option>
            </Select>
          </FilterRow>
        </FilterRow>

        {hasActiveSourceFilter && (
          <InfoNote style={{ marginBottom: 16 }}>
            Filtro activo por fuente de lead. Las columnas Conversaciones Finalizadas y Calificación reflejan el filtro.
            Las columnas de mensajes y tiempos muestran datos generales.
          </InfoNote>
        )}

        <TableScroll>
          <WidgetTable>
            <thead>
              <tr>
                <th>Usuario</th>
                <th className="align-right">Conv. Asignadas</th>
                <th className="align-right">Mensajes Enviados</th>
                <th className="align-right">Conv. Finalizadas</th>
                <th className="align-right">Tiempo Prom. Respuesta</th>
                <th className="align-right">Resp. tras Bot</th>
                <th className="align-right">Eficiencia</th>
                <th className="align-right">Calificación</th>
              </tr>
            </thead>
            <tbody>
              {topUsersData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="align-center">No hay registros de productividad para el período y canal seleccionado.</td>
                </tr>
              ) : (
                topUsersData.map((user) => {
                  const filteredData = filteredPerformanceMap?.get(user.userId);
                  const displayFinished = hasActiveSourceFilter ? filteredData?.totalFinished ?? 0 : user.finishedClients;
                  const efficiencyPct = user.outgoingMessages > 0 ? (displayFinished / user.outgoingMessages) * 100 : 0;
                  const isGoodEfficiency = user.outgoingMessages > 0 && efficiencyPct > 10;
                  const rating = hasActiveSourceFilter ? filteredData?.avgRating : (agentRatingsMap.get(user.userId) / 100) * 5;
                  const ratingPct = hasActiveSourceFilter ? ((filteredData?.avgRating || 0) / 5) * 100 : agentRatingsMap.get(user.userId);

                  return (
                    <tr key={user.userId}>
                      <td>{user.userName}</td>
                      <td className="align-right">{formatNumber(assignedConversationsMap.get(user.userId) ?? 0)}</td>
                      <td className="align-right">{formatNumber(user.outgoingMessages)}</td>
                      <td className="align-right">{formatNumber(displayFinished)}</td>
                      <td className="align-right">{formatTime(user.avgResponseTime)}</td>
                      <td className="align-right">{formatNumber(user.responsesAfterBot)}</td>
                      <td className="align-right">
                        <StatusPill variant={isGoodEfficiency ? "good" : "warn"} title={`${formatNumber(displayFinished)} / ${formatNumber(user.outgoingMessages)} × 100`}>
                          {efficiencyPct.toFixed(1)}%
                        </StatusPill>
                      </td>
                      <td className="align-right">
                        {ratingPct == null || Number.isNaN(ratingPct) ? (
                          <span>—</span>
                        ) : (
                          <StatusPill variant={ratingPct >= 80 ? "good" : ratingPct >= 50 ? "warn" : "bad"} title={`${(rating || 0).toFixed(2)} / 5.00`}>
                            {ratingPct.toFixed(1)}%
                          </StatusPill>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </WidgetTable>
        </TableScroll>
      </ChartCard>
    </WidgetSection>
  );
};

export default UserPerformanceWidgets;
